import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import usePage from "../../../../components/layouts/base-page-layout/page-hook";
import EditorPageLayout from "../../../../components/layouts/editor-page/editor-page-layout";
import { ConfirmModal } from "../../../../components/modal/components/confirm-modal/confirm-modal";
import useNavigationBlocker from "../../../../hooks/navigation-blocker-hook";
import returnMethodsListStore from "../../stores/return-methods-list-store";
import { RETURN_METHOD_TYPE } from "../../return-method-types";
import "./return-method-editor.scss";
import AdvancedCard from "./advanced-card/advanced-card";
import AddressCard from "./address-card/address-card";
import DetailsCard from "./details-card/details-card";
import ShippingLabelSettingsCard from "./shipping-label-settings-card/shipping-label-settings-card";
import ShippingInstructionsCard from "./shipping-instructions-card/shipping-instructions-card";
import useTranslation from "../../../../translation/hooks/translation-hook";
import useValidationGroup from "../../../../hooks/validation-group/validation-group-hook";
import { NotificationModal } from "../../../../components/modal/components/notification-modal/notification-modal";
import FontBody1 from "../../../../components/fonts/font-body1/font-body1";


const ReturnMethodEditor: React.FC = observer(() => {
    const params = useParams();
    const { setSubTitles, setInstruction } = usePage();
    const navigate = useNavigate();
    const vGroup = useValidationGroup();

    const [isShowUnsaved, setIsShowUnsaved] = useState(false);

    const {
        getTranslation,
        hasChanges: hasTranslationChanges,
        save: saveTranslations, isSaving: isSavingTranslations,
        discardChanges: translationsDiscardChanges, setTranslatableKeys,
    } = useTranslation();

    const returnMethodId = params.returnMethodId as string | number;
    const returnMethod = returnMethodsListStore.selectedReturnMethod;

    useEffect(() => {
        returnMethodsListStore.setSelectedReturnMethodId(returnMethodId);
        setInstruction(undefined);
        return () => {
            /** since label settings card make api calls on mount, we need to remove previous selected return method */
            returnMethodsListStore.setSelectedReturnMethodId(undefined);
        }
    }, [returnMethodId, setInstruction]);

    const translatedSubtitle = getTranslation(returnMethod?.name as string);
    useEffect(() => {
        setSubTitles(['Return Methods', translatedSubtitle]);
    }, [setSubTitles, returnMethod?.name, translatedSubtitle]);

    const keys = returnMethod ? returnMethod?.getTranslationKeys() : null;
    useEffect(() => {
        if (keys) {
            setTranslatableKeys(keys);
        }
        return () => setTranslatableKeys([]);
    }, [setTranslatableKeys, keys]);

    const hasChanges = (): boolean => {
        return !!returnMethod?.hasChanges() || hasTranslationChanges();
    }

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    const discardChanges = () => {
        returnMethod?.discardChanges();
        translationsDiscardChanges();
    }
    
    const onSaveClick = () => {
        const promised = [];

        if (returnMethod?.hasChanges()) {
            promised.push(
                returnMethod?.save()
            );
        }
        if (hasTranslationChanges()) {
            promised.push(
                 saveTranslations().then(() => returnMethod?.clearInstructionChangeNotes())
            );
        }

        Promise.all(promised).then(() => {
            unblockNavigation();
            navigate({
                pathname: `/return-methods/${returnMethod?.id}`,
            }, { replace: true });
        })
    }

    return (
        <>
            <EditorPageLayout
                toolbar={{
                    saveButton: {
                        hasChanges: hasChanges(),
                        onClick: () => onSaveClick(),
                        disabled: !hasChanges() || isSavingTranslations() || !!returnMethod?.isLoading() || !vGroup.isValid(),
                        isValid: vGroup.isValid(),
                    },
                    isLoading: isSavingTranslations() || !!returnMethod?.isLoading(),
                }}
            >
                <div className="card-gap">
                    <DetailsCard returnMethod={returnMethod} validationGroup={vGroup}/>

                    { returnMethod?.methodType === RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL &&
                        <ShippingLabelSettingsCard returnMethod={returnMethod} validationGroup={vGroup}/>
                    }

                    { !!returnMethod?.showAddressCard() &&
                        <AddressCard returnMethod={returnMethod} validationGroup={vGroup}/>

                    }

                    <ShippingInstructionsCard returnMethod={returnMethod} />

                    { !!returnMethod?.showAdvancedCard() &&
                        <AdvancedCard returnMethod={returnMethod} />
                    }
                </div>
            </EditorPageLayout>

            {isShowUnsaved &&
                <ConfirmModal
                    cancelButton={{
                        title: 'Cancel',
                        onClick: () => setIsShowUnsaved(false)
                    }}
                    okButton={{
                        title: 'Discard',
                        onClick: () => {
                            discardChanges();
                            setIsShowUnsaved(false);
                            retryNavigation();
                        }
                    }}
                />
            }
            {returnMethod?.shouldShowRatesPopup&&
                <NotificationModal
                    title='Shipping Label Settings Error'
                    text={
                        <FontBody1>Shipping settings could not be validated, please check with your provider.</FontBody1>              
                    }
                    okButton={{
                        title: 'CLOSE',
                        onClick: () => returnMethod.closeRatesPopup()
                    }}
                 />

            }
        </>
    );
});

export default ReturnMethodEditor;
